import { SearchForm } from '@/features/SearchForm'
import { BasePage } from '@/pages/BasePage'
import { css, Theme } from '@emotion/react'
import { useFetch } from '@/hooks/useFetch'
import { ChangeEventHandler, useEffect, useRef, useState } from 'react'
import type { PaginationData } from '@/types/pagination'
import { UseFetchResult } from '@/types/useFetchResult'
import { MainTitle } from '@/components/title/MainTitle'
import { UserTable } from '@/features/UserTable'
import { Users } from '@/types/users'
import { showError } from '@/shared/toast'
import { ListPagination } from '@/components/pagination/ListPagination'
import { BaseButton } from '@/components/button/BaseButton'
import { useNavigate } from 'react-router-dom'
import { SortConfig } from '@/components/table/BaseTable'

const pageStyles = (t: Theme) => css`
  display: flex;
  flex-direction: column;
`

const titleRowStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const newButtonStyle = css`
  width: 121px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const iconStyle = css`
  width: 16px;
  height: 16px;
  fill: currentColor;
  flex-shrink: 0;
`

const searchRowStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`

const tableContainerStyles = css`
  margin-top: 16px;
`

const bottomPaginationStyles = css`
  margin-top: 16px;
  display: flex;
  justify-content: right;
`

const DEFAULT_PER_PAGE = 30

export const UsersListPage = () => {
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<PaginationData<Users>>({
    data: [],
    currentPage: 1,
    lastPage: 1,
    total: 0,
    from: 0,
    to: 0,
    perPage: DEFAULT_PER_PAGE,
  })
  const [keyword, setKeyword] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'login_id',
    order: 'asc',
  })

  const paginationKeywordRef = useRef<string>('')
  const isPaginationRef = useRef<boolean>(false)

  const handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLSelectElement
  > = (event) => {
    const { name, value } = event.target

    if (name === 'keyword') {
      setKeyword(value)
    }
  }

  const handleClick = () => {
    paginationKeywordRef.current = keyword
    isPaginationRef.current = false

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))

    refetch()
  }

  const handlePageChange = (page: number) => {
    isPaginationRef.current = true
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }))

    refetch()
  }

  const handlePerPageChange = (perPage: number) => {
    isPaginationRef.current = true
    setPagination((prev) => ({
      ...prev,
      perPage,
      currentPage: 1,
    }))

    refetch()
  }

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc',
    }))
    refetch()
  }

  const { data, error, refetch }: UseFetchResult = useFetch(
    `/api/users?page=${pagination.currentPage}&per_page=${
      pagination.perPage
    }&keyword=${
      isPaginationRef.current ? paginationKeywordRef.current : keyword
    }&sort_key=${sortConfig.key}&sort_order=${sortConfig.order}`,
  )

  // TODO:  APIのレスポンス形式を統一する
  // 現在は、Destination、HealthInsurerとuserで異なるため、条件分岐している
  useEffect(() => {
    if (data && 'items' in data) {
      setPagination((prev) => ({
        ...prev,
        data: data.items,
        currentPage: data.pagination.currentPage,
        lastPage: data.pagination.lastPage,
        total: data.pagination.total,
        from: data.pagination.from,
        to: data.pagination.to,
        perPage: data.pagination.perPage,
      }))
    }
  }, [data])

  useEffect(() => {
    if (error) {
      showError('アカウント一覧の取得に失敗しました。')
    }
  }, [error])

  return (
    <BasePage title="アカウント一覧">
      <div css={pageStyles}>
        <div css={titleRowStyles}>
          <MainTitle>アカウント一覧</MainTitle>
          <BaseButton
            color="outline"
            size="fixed38"
            addStyle={newButtonStyle}
            onClick={() => navigate('new')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              css={iconStyle}
            >
              <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0 13.3-10.7 24-24 24s-24-10.7-24-24z" />
            </svg>
            新規登録
          </BaseButton>
        </div>
        <div css={searchRowStyles}>
          <SearchForm
            helpText={
              '下記の項目を対象に部分一致検索を行います' +
              '\n' +
              '「ID」「アカウント名」のいずれか'
            }
            keyword={keyword}
            onChange={handleChange}
            onClick={handleClick}
          />
          <ListPagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            from={pagination.from}
            to={pagination.to}
            total={pagination.total}
            perPage={pagination.perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </div>
        <div css={tableContainerStyles}>
          <UserTable
            data={pagination.data}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          <div css={bottomPaginationStyles}>
            <ListPagination
              currentPage={pagination.currentPage}
              lastPage={pagination.lastPage}
              from={pagination.from}
              to={pagination.to}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </div>
        </div>
      </div>
    </BasePage>
  )
}
